// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.

import { useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import Paywall from '../components/Paywall';
import { useSubscription } from '../components/SubscriptionContext';
import './Services.css';

const aiFeatures = [
  'Speech → Text (real-time)',
  'Text → Speech voiceover',
  'Face detection & image analysis',
  'Single ₹5 unlock for all AI services',
];

const SpeechToText = () => {
  const { user } = useAuth0();
  const email = user?.email;
  const { isSubscribedForEmail, isFeatureUnlocked, subscribeForFeature } = useSubscription();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isSubscribed = isSubscribedForEmail(email) || isFeatureUnlocked('ai');

  if (!isSubscribed) {
    return (
      <div className="service-page">
        <Paywall
          title="Unlock AI Services"
          description="Subscribe for ₹5 to use speech, vision, and voice tools."
          onSubscribe={() => subscribeForFeature('ai')}
          ctaLabel="Subscribe for ₹5"
          priceLabel="₹5"
          features={aiFeatures}
        />
      </div>
    );
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a WAV audio file first.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await axios.post(
        'https://eastus.stt.speech.microsoft.com/speech/recognition/conversation/cognitiveservices/v1?language=en-US',
        file,
        {
          headers: {
            'Content-Type': 'audio/wav; codecs=audio/pcm; samplerate=16000',
            'Ocp-Apim-Subscription-Key': process.env.REACT_APP_SPEECH_KEY
          }
        }
      );
      setResult(response.data.DisplayText);
    } catch (err) {
      console.error('Transcription failed:', err);
      setError('Failed to transcribe audio. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="service-page ai-stt">
      <div className="service-heading">
        <div>
          <h1>Speech to Text</h1>
          <p className="service-description">Upload WAV audio and get instant transcripts via Azure Speech.</p>
        </div>
        <div className="service-actions">
          <span className="action-chip">AI Services</span>
          <span className="action-chip">Speech → Text</span>
        </div>
      </div>

      <div className="service-grid">
        <div className="service-form">
          <label>
            Upload WAV (16kHz PCM)
            <input 
              type="file" 
              accept="audio/wav" 
              onChange={e => setFile(e.target.files[0])} 
            />
          </label>
          <button 
            onClick={handleUpload} 
            className="primary-btn"
            disabled={loading || !file}
          >
            {loading ? 'Transcribing…' : 'Transcribe Audio'}
          </button>
          {loading && (
            <div className="status-banner">
              <span className="status-icon check">★</span>
              <span>Listening and transcribing…</span>
            </div>
          )}
          {error && (
            <div className="status-banner error">
              <span className="status-icon error">!</span>
              <span>{error}</span>
            </div>
          )}
        </div>

        {result && (
          <div className="analysis-card highlight">
            <p className="analysis-label">Transcript</p>
            <p className="transcript-text">{result}</p>
          </div>
        )}
      </div>
    </div>
  );
};
export default SpeechToText;